import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { DiaryEntrySyncStatus } from 'graphql/types';
import { SmartDiaryEntry } from 'modules/smartDiary/smartDiaryNotes/type';

export interface UseSmartDiaryDirtyStateProps {
  smartEntries: SmartDiaryEntry[];
}

export interface UseSmartDiaryDirtyStateResultProps {
  isDirty: boolean;
}

export const isAnyEntrySelected = (
  smartEntries: SmartDiaryEntry[],
  values: Record<string, boolean>
): boolean =>
  smartEntries.some(
    (entry) =>
      values[entry.id] ||
      entry.syncStatus === DiaryEntrySyncStatus.DeletePending
  );

export const useSmartDiaryDirtyState = ({
  smartEntries
}: UseSmartDiaryDirtyStateProps): UseSmartDiaryDirtyStateResultProps => {
  const { watch } = useFormContext();

  const entries = useMemo(() => smartEntries.map((entry) => entry.id), [
    smartEntries
  ]);
  const formValues = watch(entries);

  const values = useMemo<Record<string, boolean>>(
    () =>
      entries.reduce(
        (acc, item, index) => ({
          ...acc,
          [`${item}`]: formValues[index] ?? false
        }),
        {}
      ),
    [entries, formValues]
  );

  const isDirty = useMemo(() => isAnyEntrySelected(smartEntries, values), [
    smartEntries,
    values
  ]);

  return useMemo(
    () => ({
      isDirty
    }),
    [isDirty]
  );
};
